
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { OptimizedAuthProvider } from "@/hooks/useOptimizedAuth";
import { OptimizedCartProvider } from "@/contexts/OptimizedCartContext";
import { ConsolidatedNotificationProvider } from "@/contexts/ConsolidatedNotificationContext";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// Import pages
import Home from "@/pages/Home";
import OptimizedProducts from "@/pages/OptimizedProducts";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import WishlistPage from "@/pages/WishlistPage";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Addresses from "@/pages/Addresses";
import Comparison from "@/pages/Comparison";
import OrderConfirmation from "@/pages/OrderConfirmation";
import OrderDetailPage from "@/pages/OrderDetailPage";
import InvoicePage from "@/pages/InvoicePage";
import Categories from "@/pages/Categories";

// Admin pages
import Admin from "@/pages/Admin";
import AdminOrders from "@/pages/AdminOrders";
import AdminUsers from "@/pages/AdminUsers";
import AdminSettings from "@/pages/AdminSettings";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminProductAnalytics from "@/pages/AdminProductAnalytics";
import AdminMLAnalytics from "@/pages/AdminMLAnalytics";
import AdminReporting from "@/pages/AdminReporting";
import AdminNotificationAnalytics from "@/pages/AdminNotificationAnalytics";
import AdminProductForm from "@/pages/AdminProductForm";
import AdminOrderDetails from "@/pages/AdminOrderDetails";
import AdminProducts from "@/pages/AdminProducts";
import AdminCategories from "@/pages/AdminCategories";
import AdminHeroContent from "@/pages/AdminHeroContent";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminOrderAnalytics from "@/pages/AdminOrderAnalytics";
import AdminEmailTemplates from "@/pages/admin/AdminEmailTemplates";
import AdminEmailLogs from "@/pages/admin/AdminEmailLogs";
import EmailAnalytics from "@/pages/admin/EmailAnalytics";
import EmailLogViewer from "@/pages/admin/EmailLogViewer";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminContactMessages from "@/pages/admin/ContactMessages";
import NewsletterConfirm from "@/pages/NewsletterConfirm";
import NewsletterUnsubscribe from "@/pages/NewsletterUnsubscribe";

import AdminLegalDocuments from "@/pages/AdminLegalDocuments";

// Legal pages
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import LegalHub from "@/pages/LegalHub";

// Admin protection
import AdminProtectedRoute from "@/components/admin/AdminProtectedRoute";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <OptimizedAuthProvider>
              <OptimizedCartProvider>
                <ConsolidatedNotificationProvider>
                  <ComparisonProvider>
                    <Router>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<OptimizedProducts />} />
                  <Route path="/categories" element={<Categories />} />
                  <Route path="/categories/:categoryId" element={<Categories />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-confirmation" element={<OrderConfirmation />} />
                  <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  
                  {/* Legal routes */}
                  <Route path="/legal" element={<LegalHub />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  
                  {/* Newsletter routes */}
                  <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
                  <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
                  
                  {/* User routes */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/:section" element={<Settings />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/:orderId" element={<OrderDetailPage />} />
                  <Route path="/invoice/:orderId" element={<InvoicePage />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/addresses" element={<Addresses />} />
                  <Route path="/comparison" element={<Comparison />} />

                  {/* Admin routes - All protected */}
                  <Route path="/admin" element={<AdminProtectedRoute><Admin /></AdminProtectedRoute>} />
                  <Route path="/admin/orders" element={<AdminProtectedRoute><AdminOrders /></AdminProtectedRoute>} />
                  <Route path="/admin/orders/:id" element={<AdminProtectedRoute><AdminOrderDetails /></AdminProtectedRoute>} />
                  <Route path="/admin/orders/analytics" element={<AdminProtectedRoute><AdminOrderAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/users" element={<AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>} />
                  <Route path="/admin/contact-messages" element={<AdminProtectedRoute><AdminContactMessages /></AdminProtectedRoute>} />
                  <Route path="/admin/products" element={<AdminProtectedRoute><AdminProducts /></AdminProtectedRoute>} />
                  <Route path="/admin/products/new" element={<AdminProtectedRoute><AdminProductForm /></AdminProtectedRoute>} />
                  <Route path="/admin/categories" element={<AdminProtectedRoute><AdminCategories /></AdminProtectedRoute>} />
                  <Route path="/admin/hero-content" element={<AdminProtectedRoute><AdminHeroContent /></AdminProtectedRoute>} />
                  <Route path="/admin/reviews" element={<AdminProtectedRoute><AdminReviews /></AdminProtectedRoute>} />
                  <Route path="/admin/settings" element={<AdminProtectedRoute><AdminSettings /></AdminProtectedRoute>} />
                  <Route path="/admin/notifications" element={<AdminProtectedRoute><AdminNotifications /></AdminProtectedRoute>} />
                  <Route path="/admin/notifications/analytics" element={<AdminProtectedRoute><AdminNotificationAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/email-templates" element={<AdminProtectedRoute><AdminEmailTemplates /></AdminProtectedRoute>} />
                  <Route path="/admin/email-logs" element={<AdminProtectedRoute><AdminEmailLogs /></AdminProtectedRoute>} />
                  <Route path="/admin/email-analytics" element={<AdminProtectedRoute><EmailAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/email-log-viewer" element={<AdminProtectedRoute><EmailLogViewer /></AdminProtectedRoute>} />
                  <Route path="/admin/newsletter" element={<AdminProtectedRoute><AdminNewsletter /></AdminProtectedRoute>} />
                  
                  <Route path="/admin/legal-documents" element={<AdminProtectedRoute><AdminLegalDocuments /></AdminProtectedRoute>} />
                  <Route path="/admin/analytics" element={<AdminProtectedRoute><AdminAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/analytics/products" element={<AdminProtectedRoute><AdminProductAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/analytics/ml" element={<AdminProtectedRoute><AdminMLAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/reporting" element={<AdminProtectedRoute><AdminReporting /></AdminProtectedRoute>} />
                  </Routes>
                    </Router>
                    <Toaster />
                    <Sonner />
                  </ComparisonProvider>
                </ConsolidatedNotificationProvider>
              </OptimizedCartProvider>
            </OptimizedAuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
